import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';

import { AuthFlowService } from '../../core/auth/auth-flow.service';
import { SessionService } from '../../core/session/session.service';
import { highestPrecedenceRole } from '../../core/session/session.model';
import type { AppRole } from '../../core/session/session.model';
import { FaIconComponent, faChevronDown, faRightFromBracket } from '../../ui/icons/app-icons.registry';

/**
 * The account affordance is a **disclosure**, not an ARIA `menu` (AITH-121 PR
 * review). A `menu` obliges the full APG interaction model — focus moved onto a
 * menuitem on open, roving ArrowUp/ArrowDown/Home/End, and only
 * menuitem/group/separator children — and this popup has exactly one action
 * beside two lines of read-only identity text. Declaring `menu` while
 * implementing disclosure behaviour is what the review flagged: screen-reader
 * users were promised keys that do nothing.
 *
 * A disclosure is the pattern that matches: `aria-expanded` + `aria-controls`
 * on the trigger, Escape to close with focus return, outside click and
 * focus-out to dismiss. The trigger takes its accessible name from its own
 * visible text (name + role) rather than an `aria-label` that replaced it,
 * so speech input can activate what it reads (WCAG 2.5.3 Label in Name).
 */
@Component({
  selector: 'app-profile-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent],
  template: `
    <div class="profile-menu">
      <button
        #trigger
        type="button"
        class="profile-trigger"
        [attr.aria-expanded]="open()"
        aria-controls="profile-popover"
        data-testid="profile-menu"
        (click)="toggle()"
      >
        <span class="profile-avatar" aria-hidden="true">{{ initials() }}</span>
        <span class="profile-summary">
          <strong>{{ displayName() }}</strong>
          <span>{{ roleLabel() }}</span>
        </span>
        <fa-icon [icon]="chevronIcon" aria-hidden="true" class="profile-chevron" />
      </button>
      @if (open()) {
        <div class="profile-popover" id="profile-popover" data-testid="profile-popover">
          <p class="profile-popover-name">{{ displayName() }}</p>
          <p class="profile-popover-role">{{ roleLabel() }}</p>
          <button type="button" data-testid="sign-out" (click)="signOut()">
            <fa-icon [icon]="signOutIcon" aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      }
    </div>
  `,
  styles: `
    :host { margin-left: auto; }
    .profile-menu { position: relative; }
    .profile-trigger { display: flex; align-items: center; gap: var(--space-xs); min-height: var(--input-height); padding: var(--space-2xs); color: var(--color-header-text); background: transparent; border: 1px solid transparent; border-radius: var(--radius-sm); cursor: pointer; text-align: left; }
    .profile-trigger:hover, .profile-trigger[aria-expanded="true"] { background: var(--color-header-interactive); }
    .profile-trigger:focus-visible { outline-color: var(--color-header-text); }
    .profile-avatar { display: grid; place-items: center; inline-size: 1.75rem; block-size: 1.75rem; border-radius: 50%; background: var(--color-accent); color: var(--color-primary); font-size: var(--font-size-xs); font-weight: var(--font-weight-bold); }
    .profile-summary { display: grid; line-height: 1.15; }
    .profile-summary strong { max-inline-size: 12rem; overflow: hidden; font-size: var(--font-size-sm); font-weight: var(--font-weight-semibold); text-overflow: ellipsis; white-space: nowrap; }
    .profile-summary span { color: var(--color-header-subtle); font-size: var(--font-size-xs); }
    .profile-chevron { font-size: var(--font-size-xs); }
    .profile-popover { position: absolute; top: calc(100% + var(--space-xs)); right: 0; z-index: var(--z-dropdown); min-inline-size: 13rem; padding: var(--space-sm); background: var(--color-surface); border: 1px solid var(--color-border-light); border-radius: var(--radius-base); box-shadow: var(--shadow-md); color: var(--color-text); }
    .profile-popover p { margin: 0; }
    .profile-popover-name { font-size: var(--font-size-sm); font-weight: var(--font-weight-semibold); }
    .profile-popover-role { margin-top: var(--space-3xs) !important; color: var(--color-text-muted); font-size: var(--font-size-xs); }
    .profile-popover button { display: flex; align-items: center; gap: var(--space-sm); width: 100%; min-height: var(--input-height); margin-top: var(--space-sm); padding: var(--space-2xs) var(--space-xs); color: var(--color-text); background: transparent; border: 0; border-top: 1px solid var(--color-border-light); cursor: pointer; text-align: left; }
    .profile-popover button:hover { background: var(--color-bg-alt); }
  `,
})
export class ProfileMenuComponent {
  private readonly session = inject(SessionService);
  private readonly flow = inject(AuthFlowService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  protected readonly chevronIcon = faChevronDown;
  protected readonly signOutIcon = faRightFromBracket;
  protected readonly open = signal(false);
  protected readonly displayName = () => this.session.session().displayName ?? 'Account';
  protected readonly initials = () => initialsFor(this.displayName());
  /**
   * The label names the *most privileged* role held, never `roles[0]`. Claim
   * order is the issuer's, `recognizedRolesFor` preserves it deliberately, and
   * `claims-mapping.spec.ts` pins that preservation — so reading position 0
   * labelled an InternalAdmin "Internal viewer" whenever Entra happened to list
   * the viewer role first (AITH-121 PR review).
   */
  protected readonly roleLabel = () => labelForRole(highestPrecedenceRole(this.session.session().roles));

  toggle(): void { this.open.update((value) => !value); }

  signOut(): void {
    this.open.set(false);
    this.flow.signOut();
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.trigger()?.nativeElement.focus();
  }

  /**
   * Tabbing out of the disclosure dismisses it. `relatedTarget` is the element
   * about to receive focus — null when focus leaves the document entirely,
   * which is not a reason to close a popup the user may come back to.
   */
  @HostListener('focusout', ['$event'])
  closeOnFocusLeaving(event: FocusEvent): void {
    if (!this.open()) return;
    const next = event.relatedTarget as Node | null;
    if (next !== null && !this.host.nativeElement.contains(next)) this.open.set(false);
  }
}

function initialsFor(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('') || 'A';
}

function labelForRole(role: AppRole | undefined): string {
  return role === 'InternalAdmin' ? 'Internal administrator'
    : role === 'InternalViewer' ? 'Internal viewer'
      : role === 'ClientAdmin' ? 'Client administrator'
        : role === 'ClientUser' ? 'Client user' : 'Account';
}